import { ChangeDetectionStrategy, Component, inject, input, output, DestroyRef, effect, signal, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  AlertComponent,
  ButtonComponent,
  InputComponent,
  ModalComponent,
} from '@verticalbridge/ui-components';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ColoUser, RequestAccessService } from '../../../core/services/request-access.service';

@Component({
  selector: 'app-edit-account-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AlertComponent,
    ButtonComponent,
    InputComponent,
    ModalComponent,
  ],
  templateUrl: './edit-account-modal.component.html',
  styleUrl: './edit-account-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditAccountModalComponent {
  private requestAccessService = inject(RequestAccessService);
  private destroyRef = inject(DestroyRef);
  private fb = inject(NonNullableFormBuilder);

  isOpen = input(false);
  userEmail = input('');
  // Display name from the auth token, used to seed first/last name when the
  // Colo record has none (VB-employee records are auto-created without them).
  displayName = input('');

  closed = output<void>();
  saved = output<ColoUser>();

  isLoading = signal(false);
  isSaving = signal(false);
  errorMessage = signal('');
  successMessage = signal('');

  // Full record last read from Colo. Edits are merged onto this on save so
  // fields the form doesn't own (roles, isInternal, contacts, etc.) survive.
  private currentUser = signal<ColoUser | null>(null);

  form = this.fb.group({
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    company: [''],
    title: [''],
    address1: [''],
    address2: [''],
    city: [''],
    state: [''],
    postalCode: ['', Validators.pattern(/^\d{5}(-\d{4})?$/)],
    phoneNumber: ['', Validators.required],
  });

  constructor() {
    // Load the current account record each time the modal opens.
    effect(() => {
      if (this.isOpen()) {
        // untracked so the load (which reads displayName) doesn't make
        // displayName a dependency — otherwise updating the name after a
        // save would re-fire this effect and reload the form.
        untracked(() => this.load());
      }
    });
  }

  private load(): void {
    this.isLoading.set(true);
    this.errorMessage.set('');
    this.successMessage.set('');
    this.form.enable();

    this.requestAccessService
      .getColoUserData()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: user => {
          this.currentUser.set(user);
          const name = this.displayName();
          const nameParts = name && name !== 'User' ? name.trim().split(/\s+/) : [];
          const fallbackFirst = nameParts[0] ?? '';
          const fallbackLast = nameParts.slice(1).join(' ');
          this.form.reset({
            firstName: user?.firstName || fallbackFirst,
            lastName: user?.lastName || fallbackLast,
            company: user?.company ?? '',
            title: user?.title ?? '',
            address1: user?.address1 ?? '',
            address2: user?.address2 ?? '',
            city: user?.city ?? '',
            state: user?.state ?? '',
            postalCode: user?.postalCode ?? '',
            phoneNumber: user?.phoneNumber ?? '',
          });
          this.isLoading.set(false);
        },
        error: () => {
          this.errorMessage.set('Could not load your account information. Please try again.');
          this.isLoading.set(false);
        },
      });
  }

  save(): void {
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      this.errorMessage.set('Please fill in all required fields.');
      return;
    }

    this.isSaving.set(true);
    this.errorMessage.set('');
    this.form.disable();

    const values = this.form.getRawValue();
    const merged: ColoUser = {
      ...(this.currentUser() ?? {}),
      userEmail: this.userEmail(),
      firstName: values.firstName,
      lastName: values.lastName,
      company: values.company,
      title: values.title,
      address1: values.address1,
      address2: values.address2,
      city: values.city,
      state: values.state,
      postalCode: values.postalCode,
      phoneNumber: values.phoneNumber,
    };

    this.requestAccessService
      .updateColoUser(merged)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isSaving.set(false);
          this.successMessage.set('Your account information has been updated.');
          this.saved.emit(merged);
        },
        error: () => {
          this.isSaving.set(false);
          this.form.enable();
          this.errorMessage.set('Failed to update your account. Please try again.');
        },
      });
  }

  close(): void {
    this.errorMessage.set('');
    this.successMessage.set('');
    this.closed.emit();
  }

  // vb-modal emits visibleChange(false) for X / backdrop / Escape, some of
  // which don't fire onCancel — reset the parent's open flag here too.
  onVisibleChange(visible: boolean): void {
    if (!visible) {
      this.close();
    }
  }
}
